import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Keyboard, Mic, Square } from 'lucide-react';
import type { Instrument, ProjectReader } from '../lib/types';
import { readerSnapshot } from '../services/readers';
import { tidyTranscript } from '../hooks/useSpeech';
import { useRecorder } from '../hooks/useRecorder';
import { uploadRecording } from '../services/storage';
import { createCapture, newCaptureId, setCaptureFailed, setExtraction } from '../services/captures';
import { useAuth } from '../hooks/useAuth';
import { runExtraction, transcribeRecording } from '../services/extract';
import { Button, Modal, cx } from './ui';
import type { ConfirmationSource } from './ConfirmationScreen';

/** What to say for a reader: a sample name, then each field with a value. */
function readerExample(reader: ProjectReader): string {
  const parts = reader.fields
    .slice(0, 3)
    .map((f, i) => `${f.label.toLowerCase()} ${['twelve point five', 'zero point three', 'forty one'][i]}`);
  return ['sample one', ...parts].join(', ');
}

export default function VoiceCapture({
  open,
  onClose,
  uid,
  projectId,
  instrument,
  onCaptured,
  reader,
  onReaderVoice,
}: {
  open: boolean;
  onClose: () => void;
  uid: string;
  projectId: string;
  instrument: Instrument;
  onCaptured: (source: ConfirmationSource) => void;
  /**
   * In a project that records against readers, the dictation is read against
   * this reader's fields and handed to `onReaderVoice` instead of going through
   * the fixed-field path.
   */
  reader?: ProjectReader | null;
  onReaderVoice?: (captureId: string, transcript: string) => void;
}) {
  const recorder = useRecorder();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [edited, setEdited] = useState<string | null>(null);
  const [transcript, setTranscript] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** One id for the whole take, so the audio and the capture share a name. */
  const [captureId, setCaptureId] = useState(() => newCaptureId(projectId));
  const stoppingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setEdited(null);
      setTranscript('');
      setError(null);
      setCaptureId(newCaptureId(projectId));
      void recorder.start();
    } else {
      recorder.cancel();
    }
    // Driven by `open` alone; the recorder's identity changes every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Stop is the only thing that ends a take, and it is also what produces the
   * words: the recording runs untouched until here, then goes up once and comes
   * back transcribed. Nothing is listening for silence, so a pause of any
   * length is simply part of the recording.
   */
  async function stopAndTranscribe() {
    // A second tap while the first is still settling would upload and
    // transcribe the same take twice.
    if (stoppingRef.current) return;
    stoppingRef.current = true;
    const rec = await recorder.stop();
    if (!rec) {
      setError('Nothing was recorded.');
      return;
    }
    setTranscribing(true);
    setError(null);
    try {
      const path = await uploadRecording(uid, captureId, rec.blob, rec.mimeType);
      setTranscript(await transcribeRecording(projectId, path, rec.mimeType));
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not transcribe that recording.');
    } finally {
      setTranscribing(false);
      stoppingRef.current = false;
    }
  }

  const heard = edited ?? transcript;
  const elapsed = recorder.seconds;

  async function submit() {
    const transcript = tidyTranscript(heard);
    if (!transcript) return;

    setBusy(true);
    if (reader && onReaderVoice) {
      try {
        await createCapture(projectId, captureId, {
          mode: 'voice',
          instrument: 'unknown',
          capturedByUid: uid,
          capturedByName: user?.displayName ?? user?.email ?? null,
          transcript,
          reader: readerSnapshot(reader),
          status: 'pending',
        });
        onClose();
        onReaderVoice(captureId, transcript);
      } catch (e) {
        setError((e as Error)?.message ?? 'Could not save that dictation.');
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      // The raw transcript is stored exactly as the photo path stores the image.
      await createCapture(projectId, captureId, {
        mode: 'voice',
        instrument,
        capturedByUid: uid,
        capturedByName: user?.displayName ?? user?.email ?? null,
        transcript,
        status: 'pending',
      });

      const base: ConfirmationSource = {
        mode: 'voice',
        captureId,
        instrument,
        extraction: null,
        transcript,
        previewUrl: null,
        storagePath: null,
        targetFormulationId: null,
        focusKey: null,
        extracting: true,
        progress: { phase: 'reading' },
      };
      onClose();
      onCaptured(base);

      try {
        const result = await runExtraction({
          projectId,
          captureId,
          mode: 'voice',
          transcript,
          instrument,
        });
        await setExtraction(projectId, captureId, result);
        onCaptured({ ...base, extraction: result, extracting: false, progress: undefined });
      } catch (e) {
        const message = (e as Error)?.message ?? 'Could not parse that.';
        await setCaptureFailed(projectId, captureId, message);
        onCaptured({ ...base, extracting: false, progress: undefined, error: message });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Speak the values">
      <div className="space-y-4">
        <div
          className={cx(
            'flex min-h-28 flex-col justify-center rounded-xl border p-4 text-center transition-colors',
            recorder.recording ? 'border-accent-500 bg-accent-500/10' : 'border-ink-700 bg-ink-800',
          )}
        >
          {recorder.recording && (
            <div className="mb-2 flex items-center justify-center gap-2 text-xs font-medium text-accent-400">
              <span className="relative flex size-2">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-accent-400 opacity-75" />
                <span className="relative inline-flex size-2 rounded-full bg-accent-500" />
              </span>
              Recording
              <span className="tabular-nums text-ink-400">
                {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
              </span>
              {/* A live level meter: a microphone that is hearing nothing is
                  visible immediately, rather than after two minutes of talking
                  into a muted device. */}
              <span className="flex h-3 items-end gap-0.5" aria-hidden>
                {[0.15, 0.4, 0.7].map((threshold) => (
                  <span
                    key={threshold}
                    className={cx(
                      'w-0.5 rounded-full transition-all duration-75',
                      recorder.level > threshold ? 'h-3 bg-accent-400' : 'h-1 bg-ink-600',
                    )}
                  />
                ))}
              </span>
            </div>
          )}

          {heard ? (
            <p className="font-mono text-sm leading-relaxed text-ink-100">{heard}</p>
          ) : (
            <p className="text-sm text-ink-400">
              {recorder.recording
                ? 'Recording — take as long as you need. Pauses are fine.'
                : transcribing
                  ? 'Working out what you said…'
                  : 'Tap the microphone to start.'}
            </p>
          )}
        </div>

        {(recorder.error || error) && (
          <div className="flex gap-2 rounded-lg border border-amber-800 bg-amber-950 p-3 text-sm text-amber-200">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>{recorder.error ?? error}</span>
          </div>
        )}

        {heard && !recorder.recording && (
          <div>
            <label className="field-label mb-1.5" htmlFor="transcript-edit">
              Correct the transcript if it misheard you
            </label>
            <textarea
              id="transcript-edit"
              rows={3}
              value={heard}
              onChange={(e) => setEdited(e.target.value)}
              className="w-full rounded-lg border border-ink-600 bg-ink-800 px-3 py-2 font-mono text-sm text-ink-100 focus:border-accent-500 focus:outline-none focus:ring-1 focus:ring-accent-500"
            />
            <p className="mt-1 text-xs text-ink-400">
              Both this text and the values it produces go to the confirmation screen.
            </p>
          </div>
        )}

        <p className="rounded-lg bg-ink-800/60 p-2.5 text-xs text-ink-400">
          {reader && (
            <>
              <span className="font-medium text-ink-300">Try:</span> “{readerExample(reader)}”
              <br />
            </>
          )}
          Say “minus” for a negative value — it is the one word worth over-pronouncing.
        </p>

        <div className="flex gap-2">
          {recorder.recording ? (
            <Button
              variant="danger"
              className="flex-1"
              icon={<Square size={16} />}
              onClick={() => void stopAndTranscribe()}
            >
              Stop
            </Button>
          ) : (
            <Button
              variant="secondary"
              className="flex-1"
              icon={<Mic size={16} />}
              loading={transcribing}
              disabled={transcribing || busy}
              onClick={() => void recorder.start()}
            >
              {heard ? 'Record again' : 'Start'}
            </Button>
          )}
          <Button
            variant="primary"
            className="flex-1"
            loading={busy}
            disabled={!heard.trim() || recorder.recording || transcribing}
            onClick={() => void submit()}
          >
            Read values
          </Button>
        </div>

        <button
          onClick={onClose}
          className="flex w-full items-center justify-center gap-1.5 text-xs text-ink-400 hover:text-ink-100"
        >
          <Keyboard size={12} />
          Type them instead
        </button>
      </div>
    </Modal>
  );
}
